import { ActionResult } from '~/hooks/actions/enums';
import type { ActionHandler } from '~/hooks/actions/types';
import logger from '~/services/logger';
import type { FeedEntry } from '~/services/layout/index.ts';
import {
  QueueManager,
  setupClientQueue,
  defaultFeedResolver,
} from '../index.ts';
import { dispatchAudioPlayerOpen } from '~/utils/audio-player';

type AddAllToQueueOptions = {
  url?: string;
  entries?: FeedEntry[];
  items?: FeedEntry[];
  position?: 'top' | 'bottom';
  startPlayback?: boolean;
};

/**
 * Appends a collection of queue items to QueueManager, matching QuickBrick queue-action behavior.
 */
export const addAllToQueueAction: ActionHandler<AddAllToQueueOptions> = async (
  action
) => {
  setupClientQueue();

  const options = action.options ?? {};
  const contextEntry = (
    action.context as
      | { entry?: FeedEntry & { entry?: FeedEntry[] } }
      | undefined
  )?.entry;

  let entries: FeedEntry[] = [];

  if (Array.isArray(options.entries)) {
    entries = options.entries;
  } else if (Array.isArray(options.items)) {
    entries = options.items;
  } else if (contextEntry && Array.isArray(contextEntry.entry)) {
    entries = contextEntry.entry;
  } else {
    const url = options.url || contextEntry?.content?.src;

    if (!url) {
      return ActionResult.Error;
    }

    try {
      const resolver =
        QueueManager.instance.feedResolver || defaultFeedResolver;
      entries = await resolver(url, contextEntry);
    } catch (resolverError) {
      logger.error(
        `addAllToQueueAction: failed to resolve feed for url ${url}: ${resolverError}`
      );
      return ActionResult.Error;
    }
  }

  const validEntries = entries.filter((e) => Boolean(e?.id));

  if (validEntries.length === 0) {
    return ActionResult.Error;
  }

  try {
    const firstEntry = await QueueManager.instance.addAllToQueue(validEntries, {
      position: options.position,
      startPlayback: options.startPlayback,
    });

    if (options.startPlayback && firstEntry) {
      dispatchAudioPlayerOpen({ entry: firstEntry });
    }

    return ActionResult.Success;
  } catch (error) {
    logger.error(`addAllToQueueAction: failed to add items: ${error}`);
    return ActionResult.Error;
  }
};
